// src/services/admin.ts
import api from "../lib/api";

export interface AdminStats {
  totalUsers: number;
  totalExperts: number;
  totalIndustries: number;
  pendingTestimonials: number;
  approvedUsers: number;
  pendingUsers: number;
}

/**
 * Get admin dashboard statistics
 */
export async function getStats(): Promise<AdminStats> {
  const { data } = await api.get('/api/admin/stats');
  return data.data.stats;
}

/**
 * Invite user to join the platform
 */
export interface InviteUserPayload {
  email: string;
  userType: 'member' | 'expert';
}

export async function inviteUser(payload: InviteUserPayload) {
  const { data } = await api.post('/api/admin/invite-user', payload);
  return data;
}

// ============================================
// ADMIN MANAGEMENT API FUNCTIONS
// ============================================

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  createdAt: string;
  permissions: string[];
}

export interface CreateAdminPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  isSuperAdmin?: boolean;
  permissions?: string[];
}

export interface UpdatePermissionsPayload {
  permissions: string[];
}

export interface ToggleStatusPayload {
  isActive: boolean;
}

/**
 * Create a new admin user (Super Admin only)
 */
export async function createAdmin(payload: CreateAdminPayload) {
  const { data } = await api.post('/api/admin/admins', payload);
  return data;
}

/**
 * List all admin users (Super Admin only)
 */
export async function listAdmins(): Promise<AdminUser[]> {
  const { data } = await api.get('/api/admin/admins');
  return data.data.admins;
}

/**
 * Update admin permissions (Super Admin only)
 */
export async function updateAdminPermissions(adminId: string, payload: UpdatePermissionsPayload) {
  const { data } = await api.put(`/api/admin/admins/${adminId}/permissions`, payload);
  return data;
}

/**
 * Toggle admin status - activate/deactivate (Super Admin only)
 */
export async function toggleAdminStatus(adminId: string, payload: ToggleStatusPayload) {
  const { data } = await api.patch(`/api/admin/admins/${adminId}/status`, payload);
  return data;
}

/**
 * Delete admin user (Super Admin only)
 */
export async function deleteAdmin(adminId: string) {
  const { data } = await api.delete(`/api/admin/admins/${adminId}`);
  return data;
}

// ============================================
// SETTINGS & SECURITY API FUNCTIONS
// ============================================

export interface SystemSettings {
  [key: string]: any;
}

export interface SecurityLog {
  id: string;
  userId?: string;
  actorId?: string;
  action: string;
  category: string;
  resourceType?: string;
  resourceId?: string;
  ipAddress?: string;
  userAgent?: string;
  status: string;
  details?: any;
  createdAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    userType: string;
  };
  actor?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    userType: string;
  };
}

export interface SecurityStats {
  totalLogs: number;
  successfulLogins: number;
  failedLogins: number;
  passwordChanges: number;
  otherActions: number;
}

/**
 * Get all system settings (Super Admin only)
 */
export async function getSettings(): Promise<SystemSettings> {
  const { data } = await api.get('/api/admin/settings');
  return data.data;
}

/**
 * Update system settings (Super Admin only)
 */
export async function updateSettings(settings: Record<string, {value: any, type: string, description?: string}>) {
  const { data } = await api.put('/api/admin/settings', { settings });
  return data;
}

/**
 * Get security logs with comprehensive filtering
 */
export async function getSecurityLogs(params?: {
  limit?: number;
  offset?: number;
  category?: string;
  action?: string;
  status?: string;
  userId?: string;
  actorId?: string;
  resourceType?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ logs: SecurityLog[]; total: number; limit: number; offset: number }> {
  const { data } = await api.get('/api/admin/security/logs', { params });
  return data.data;
}

/**
 * Get security statistics
 */
export async function getSecurityStats(days: number = 7): Promise<SecurityStats> {
  const { data } = await api.get('/api/admin/security/stats', { params: { days } });
  return data.data;
}

/**
 * Get real-time system information
 */
export interface SystemInfo {
  version: string;
  database: {
    status: string;
    responseTime: string;
  };
  uptime: string;
  memory: {
    used: string;
    total: string;
    percentage: number;
  };
  system: {
    platform: string;
    nodeVersion: string;
    totalMemory: string;
    freeMemory: string;
    cpuCores: number;
  };
  users: {
    total: number;
    active: number;
  };
  environment: string;
  timestamp: string;
}

export async function getSystemInfo(): Promise<SystemInfo> {
  const { data } = await api.get('/api/admin/system-info');
  return data.data;
}

// ============================================
// VALUE CHAIN & SUB-INDUSTRY AUTO-CREATION
// ============================================

export interface SyncSubIndustriesResult {
  created: number;
  subIndustries: Array<{
    id: string;
    name: string;
    description: string;
    industryId: string;
    parentSubIndustryId?: string;
  }>;
}

/**
 * Auto-create sub-industries from a specific value chain stage's items
 */
export async function syncSubIndustriesFromStage(stageId: string): Promise<SyncSubIndustriesResult> {
  const { data } = await api.post(`/api/admin/value-chain-stages/${stageId}/sync-sub-industries`);
  return data.data;
}

/**
 * Sync all sub-industries from all value chain stages for an industry
 */
export async function syncAllSubIndustries(industryId: string, subIndustryId?: string): Promise<{ totalCreated: number; subIndustries: any[] }> {
  const params = subIndustryId ? { subIndustryId } : {};
  const { data } = await api.post(`/api/admin/industries/${industryId}/sync-all-sub-industries`, {}, { params });
  return data.data;
}

/**
 * Get mapping of value chain items to their associated sub-industries
 */
export async function getValueChainItemsMapping(industryId: string, subIndustryId?: string): Promise<Record<string, { id: string; name: string; description: string }>> {
  const params = subIndustryId ? { subIndustryId } : {};
  const { data } = await api.get(`/api/admin/industries/${industryId}/value-chain-items-mapping`, { params });
  return data.data.itemMap;
}
